import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  AUTH_TOKEN,
  USERNAME,
  USER_EMAIL,
  AUTH_EXPIRES_IN,
  REFRESH_TOKEN,
  USER_ROLES,
} from "constants/AuthConstant";
import AuthService from "services/AuthService";
import { setRefreshTokenTimer } from "auth/FetchInterceptor";

const loadRoles = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_ROLES) || "[]");
  } catch {
    return [];
  }
};

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: localStorage.getItem(AUTH_TOKEN) || null,
  roles: loadRoles(),
};

export const signIn = createAsyncThunk(
  "auth/signIn",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.login(data);
      if (response.accessToken) {
        localStorage.setItem(AUTH_TOKEN, String(response.accessToken));
        localStorage.setItem(USERNAME, response.username);
        localStorage.setItem(USER_EMAIL, response.email);
        localStorage.setItem(AUTH_EXPIRES_IN, response.tokenExpiry);
        localStorage.setItem(REFRESH_TOKEN, String(response.refreshToken));
        localStorage.setItem(
          USER_ROLES,
          JSON.stringify(response.roles || [])
        );

        const tokenExpirySeconds =
          (response.tokenExpiry - new Date().getTime()) / 1000 - 60;
        setRefreshTokenTimer(tokenExpirySeconds);

        return {
          token: response.accessToken,
          roles: response.roles || [],
        };
      } else {
        return rejectWithValue("No access token received");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Invalid username or password. Please check and try again.";
      return rejectWithValue(message);
    }
  }
);

// export const signUp = createAsyncThunk('auth/signUp',async (data, { rejectWithValue }) => {
// 	const { email, password } = data
// 	try {
// 		const response = await FirebaseService.signUpEmailRequest(email, password)
// 		if (response.user) {
// 			const token = response.user.refreshToken;
// 			localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
// 			return token;
// 		} else {
// 			return rejectWithValue(response.message?.replace('Firebase: ', ''));
// 		}
// 	} catch (err) {
// 		return rejectWithValue(err.message || 'Error')
// 	}
// })

export const signOut = createAsyncThunk("auth/signOut", async () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(USERNAME);
  localStorage.removeItem(USER_EMAIL);
  localStorage.removeItem(AUTH_EXPIRES_IN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem(USER_ROLES);
  window.location.reload();
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.roles = [];
      state.redirect = "/";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload.token;
        state.roles = action.payload.roles;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.roles = [];
        state.redirect = "/";
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.roles = [];
        state.redirect = "/";
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
} = authSlice.actions;

export default authSlice.reducer;
